import type { BuildStatus, Image, LinkRef } from './schema'

export const workPage = {
  eyebrow: 'Work',
  title: 'Funnels, websites and automations built in HighLevel.',
  lede: 'Live sites built in GoHighLevel, and workflows from client accounts in the HighLevel builder.',
}

/**
 * A cover with its 480 / 640 / 960 copies (scripts/make-image-variants.mjs). Copies wider than the
 * original are skipped by that script, so `widths` lists only the ones that exist.
 */
export const cover = (id: string, alt: string, width: number, height: number, widths = [480, 640, 960]): Image => ({
  src: `/images/covers/${id}.webp`,
  alt,
  width,
  height,
  srcSet: widths.map((w) => `/images/covers/${w}/${id}.webp ${w}w`).join(', ') + `, /images/covers/${id}.webp ${width}w`,
})

/* ---------- Websites: live sites built in GoHighLevel (v1 src/data/funnels.ts) ---------- */

export type SiteBuild = { id: string; label: string; summary: string; url: string }

export const liveSites: SiteBuild[] = [
  { id: 'genesis-m100', label: 'Genesis M100', summary: 'Professional landing page with a modern design.', url: 'https://genesism100.com/' },
  { id: 'ai-business-growth-now', label: 'AI Business Growth Now', summary: 'AI-powered business growth offer and funnel.', url: 'https://www.aibusinessgrowthnow.com/' },
  { id: 'aiorbye', label: 'Aiorbye', summary: 'Website for an AI automation and business services agency.', url: 'https://www.aiorbye.com/' },
  { id: 'philosoraptor-meme', label: 'Philosoraptor Meme', summary: 'Creative meme generator platform.', url: 'https://philosoraptormeme.com/' },
]

/* ---------- Automations: workflows from client accounts (v1 public/work + src/data/ai-stack.ts) ---------- */

export type AutomationBuild = {
  id: string
  name: string
  kicker: string
  summary: string
  stack: string
  status: BuildStatus
  /** The workflow page in public/demos/plans, with its builder screenshot. Voice AI has none. */
  href?: string
  image?: Image
}

export const automations: AutomationBuild[] = [
  {
    id: 'wf-nurture-webscrape',
    name: 'Nurture AI with web scraping',
    kicker: 'AI nurture',
    summary: 'An automated lead-nurturing workflow that uses AI-powered web scraping to personalise outreach for each contact.',
    stack: 'HighLevel workflows + AI',
    status: 'Live',
    href: '/demos/plans/wf-nurture-webscrape.html',
    image: cover('wf-nurture-webscrape', 'Nurture AI with web scraping, in the HighLevel workflow builder', 854, 903, [480, 640]),
  },
  {
    id: 'wf-community-nurture',
    name: 'Community nurture sequence',
    kicker: 'AI nurture',
    summary: 'An AI-generated email sequence that engages unpaid community members and moves them towards conversion.',
    stack: 'HighLevel workflows + AI email',
    status: 'Live',
    href: '/demos/plans/wf-community-nurture.html',
    image: cover('wf-community-nurture', 'Community nurture sequence, in the HighLevel workflow builder', 1768, 891),
  },
  {
    id: 'wf-whatsapp-ivr',
    name: 'WhatsApp IVR system',
    kicker: 'Conversation routing',
    summary: 'Interactive voice response for WhatsApp, with intelligent routing that sends each enquiry down the right path.',
    stack: 'HighLevel workflows + WhatsApp',
    status: 'Live',
    href: '/demos/plans/wf-whatsapp-ivr.html',
    image: cover('wf-whatsapp-ivr', 'WhatsApp IVR system, in the HighLevel workflow builder', 1801, 899),
  },
  {
    id: 'voice-ai-agents',
    name: 'Voice AI agents',
    kicker: 'Voice AI',
    summary: 'Answers calls, qualifies callers and routes them to the right next step.',
    stack: 'HighLevel Voice AI',
    status: 'Live',
  },
]

/* ---------- The DISC and Motivators profile (v1 public/work/disc-profile.html) ---------- */

export const discProfile = {
  title: 'DISC and Motivators profile',
  link: { label: 'Open the full profile', href: '/demos/plans/disc-profile.html', external: true } satisfies LinkRef,
}

/** Number of builds across every Work chapter, shown as the sidebar badge. Derived, never typed in. */
export const workCount = liveSites.length + automations.length + 1
